import { GetMeasurementsStatsResponseSchema, type GetMeasurementsStatsResponse } from '@weft/api-definition';
import type { Optional } from '@weft/utils';
import { isNotNullOrUndefined } from '@weft/utils';
import { useQuery } from 'react-query';
import { getPrivateApi } from '../../../services/api';
import type { CustomUseQueryOptions } from '../requests-types/custom-use-query-options';
import type { GetMeasurementsStatsRequest } from './use-measurements-stats-types/get-measurements-stats-request';

type UseMeasurementsStatsInput = {
  startTime: Optional<Date>,
  endTime: Optional<Date>,
  customersIds: Optional<string[]>,
  eventNames: Optional<string[]>,
  metersIds: Optional<string[]>,  
};

export function useMeasurementsStats(useMeasurementsStatsInput: UseMeasurementsStatsInput, options?: CustomUseQueryOptions<GetMeasurementsStatsResponse>) {
  const {
    startTime,
    endTime,
    customersIds,
    eventNames,
    metersIds,  
  } = useMeasurementsStatsInput;
  const api = getPrivateApi();

  const isParamsValid = isNotNullOrUndefined(startTime) && isNotNullOrUndefined(endTime) 
  && (isNotNullOrUndefined(customersIds) || isNotNullOrUndefined(eventNames));

  const queryKey = buildQueryKey(
    startTime,
    endTime,
    customersIds,
    eventNames,
    metersIds,
  );  

  const getMeasurementsStatsRequest = buildGetMeasurementsStatsRequest(startTime, endTime, customersIds, eventNames, metersIds);

  return useQuery(
    queryKey, 
    async () => {
      const getMeasurementsStatsResponse = await api.getMeasurementsStats(getMeasurementsStatsRequest);

      return GetMeasurementsStatsResponseSchema.parse(getMeasurementsStatsResponse);
    },
    {
      enabled: isParamsValid,
      ...options,
    },
  );
}

function buildGetMeasurementsStatsRequest(startTime: Optional<Date>, endTime: Optional<Date>, customersIds: Optional<string[]>, eventNames: Optional<string[]>, metersIds: Optional<string[]>): GetMeasurementsStatsRequest {
  return {
    startTime: startTime!,
    endTime: endTime!,
    customersIds: isNotNullOrUndefined(customersIds) ? JSON.stringify(customersIds) : undefined,
    eventNames: isNotNullOrUndefined(eventNames) ? JSON.stringify(eventNames) : undefined,
    metersIds: isNotNullOrUndefined(metersIds) ? JSON.stringify(metersIds) : undefined,
  };
}

function buildQueryKey(
  startTime: Optional<Date>,
  endTime: Optional<Date>,
  customerIds: Optional<string[]>,
  eventNames: Optional<string[]>,
  meterIds: Optional<string[]>): string[] {
  return [
    'measurements',
    startTime?.toString() ?? '',
    endTime?.toString() ?? '',
    ...(customerIds ?? []),
    ...(eventNames ?? []),
    ...(meterIds ?? []),
  ];
}